import { useEffect, useMemo, useRef, useState } from 'react';
import { api, type HubInfo, type HubPipeline, type Job, type ProjectCard, type Relay } from '@/api/client';
import { formatBytes } from './format';
import { acceptsScan, uploadScan } from './upload';

/** Client flags the hub lets us pass, with the wording the desktop app uses. `--frame` is a
 *  field of its own; the rest are switches. Unknown flags the hub allows show by name. */
const SWITCH_LABELS: Record<string, string> = {
  '--disable-tta': 'Disable test-time augmentation (faster, slightly less accurate)',
  '--no-parallel': 'Run the pipeline stages one at a time (lower memory)',
  '--export-slicer': 'Also export a 3D Slicer scene',
  '--leaflet-analysis': 'Leaflet analysis (cusp segmentation, thickness fields)',
  '--body-stats': 'Body stats and z-scores (needs a TotalSegmentator licence on the worker)',
  '--full-frames': 'Keep the full uncropped CT volume in the export',
  '--all-frames': 'Process every frame of a 4D series',
  '--frame-registration': 'Register frames to the primary frame',
  '--no-cutaway-clean': 'Skip the clean cutaway volume',
  '--no-leaflet-fields': 'Skip the leaflet field volumes',
  '--leaflets': 'Segment the leaflets',
  '--no-leaflets': 'Skip leaflet segmentation',
};

type Phase = 'form' | 'creating' | 'uploading' | 'submitting' | 'done';

export function RunPipelineDialog({
  hub,
  project,
  onClose,
  onSubmitted,
}: {
  hub: HubInfo;
  /** Existing project to run against; omit to create a new one from the scan. */
  project?: { id: string; name: string } | null;
  onClose: () => void;
  onSubmitted: (projectId: string, job: Job) => void;
}) {
  const creating = !project;
  const [name, setName] = useState(project?.name ?? '');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const pipelines = hub.pipelines.filter((p) => p.versions.some((v) => v.state === 'available'));
  const [pipelineName, setPipelineName] = useState(
    () => pipelines.find((p) => p.name === hub.default_pipeline)?.name ?? pipelines[0]?.name ?? '',
  );
  const pipeline: HubPipeline | undefined = pipelines.find((p) => p.name === pipelineName);
  const [version, setVersion] = useState<string>('');
  const chosenVersion = useMemo(
    () => pipeline?.versions.find((v) => v.version === (version || pipeline.default_version)) ?? pipeline?.versions.find((v) => v.is_default) ?? pipeline?.versions[0],
    [pipeline, version],
  );
  const allowed = chosenVersion?.allowed_client_args ?? [];
  // Reports the worker renders right after the run (hub "Reports"): every one the version
  // offers is on unless unticked, so a version switch needs no reset.
  const reportOptions = chosenVersion?.reports ?? [];
  const [reportsOff, setReportsOff] = useState<Record<string, boolean>>({});
  const [frame, setFrame] = useState('');
  const [worker, setWorker] = useState(''); // '' = let the hub choose
  const [switches, setSwitches] = useState<Record<string, boolean>>({});
  const [phase, setPhase] = useState<Phase>('form');
  const [progress, setProgress] = useState<[number, number]>([0, 0]);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file && name === '' && creating) return;
    if (file && creating && name === '') setName(file.name.replace(/\.(nii\.gz|nii|zip)$/i, ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const busy = phase !== 'form' && phase !== 'done';
  const ready = !!file && (!creating || name.trim().length > 0) && !!pipeline && hub.online;

  function pickFile(f: File | null) {
    setError(null);
    if (f && !acceptsScan(f.name, hub.upload.accept)) {
      setError(`Upload a NIfTI file (.nii, .nii.gz) or a zip of a DICOM series, not ${f.name}`);
      setFile(null);
      return;
    }
    if (f && f.size > hub.upload.max_bytes) {
      setError(`File is larger than ${formatBytes(hub.upload.max_bytes)}`);
      setFile(null);
      return;
    }
    setFile(f);
  }

  function extraArgs(): string[] {
    const args: string[] = [];
    const n = frame.trim();
    if (n !== '' && allowed.includes('--frame')) args.push('--frame', String(parseInt(n, 10)));
    for (const [flag, on] of Object.entries(switches)) if (on && allowed.includes(flag)) args.push(flag);
    return args;
  }

  function selectedReports(): string[] {
    return reportOptions.filter((r) => !reportsOff[r.name]).map((r) => r.name);
  }

  async function go() {
    if (!file || !pipeline) return;
    setError(null);
    abort.current = new AbortController();
    let projectId = project?.id ?? '';
    try {
      if (creating) {
        setPhase('creating');
        const created = await api.post<ProjectCard>('/api/projects', { name: name.trim(), notes });
        projectId = created.id;
      }
      setPhase('uploading');
      const ticket = await uploadScan(file, (sent, total) => setProgress([sent, total]), abort.current.signal);
      setPhase('submitting');
      setProgress([0, file.size]);
      let relay = await api.post<Relay>(`/api/projects/${projectId}/jobs`, {
        upload_id: ticket.upload_id,
        pipeline: pipeline.name,
        version: chosenVersion?.version ?? null,
        extra_args: extraArgs(),
        worker: worker || null,
        reports: selectedReports(),
      });
      // The backend streams the object to the hub in the background; poll its byte count.
      while (relay.state === 'relaying') {
        setProgress([relay.sent, relay.total]);
        await new Promise((r) => setTimeout(r, 500));
        relay = await api.get<Relay>(`/api/uploads/${ticket.upload_id}/relay`);
      }
      if (relay.state === 'failed' || !relay.job) throw new Error(relay.error ?? 'the hub did not accept the job');
      setProgress([relay.total, relay.total]);
      setPhase('done');
      onSubmitted(projectId, relay.job);
    } catch (e) {
      const err = e as Error;
      setPhase('form');
      if (err.name === 'AbortError') setError(creating && projectId ? 'Cancelled. The empty project was kept; delete it or run the pipeline again.' : 'Cancelled.');
      else setError(err.message + (creating && projectId ? ' The project was created; open it and run the pipeline again.' : ''));
    }
  }

  const [sent, total] = progress;
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal run-dialog" onClick={(e) => e.stopPropagation()}>
        <h2>{creating ? 'New project' : `Run pipeline on ${project?.name}`}</h2>
        {!hub.online && (
          <div className="error">
            The pipeline hub is not reachable{hub.error ? `: ${hub.error}` : ''}. Submitting is disabled.
          </div>
        )}
        {hub.online && hub.error && (
          <div className="error">
            The hub answered but refused the request: {hub.error}. Check the hub token setting on the backend.
          </div>
        )}
        {hub.online && hub.workers_online === 0 && (
          <div className="notice">No worker is online right now. The job will queue until one connects.</div>
        )}

        <h3>Scan</h3>
        <div
          className={`dropzone${file ? ' has-file' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!busy) pickFile(e.dataTransfer.files[0] ?? null);
          }}
          onClick={() => !busy && fileInput.current?.click()}
        >
          <input
            ref={fileInput}
            type="file"
            accept={hub.upload.accept.join(',') + ',application/zip,application/gzip'}
            hidden
            disabled={busy}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <>
              <div className="mono">{file.name}</div>
              <div className="muted small">{formatBytes(file.size)} · click to change</div>
            </>
          ) : (
            <>
              <div>Drop a NIfTI file (.nii, .nii.gz) or a zip of one DICOM series, or click to choose</div>
              <div className="muted small">Up to {formatBytes(hub.upload.max_bytes)}. The file goes straight to S3.</div>
            </>
          )}
        </div>

        {creating && (
          <>
            <h3>Project</h3>
            <label className="field">
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={busy} maxLength={200} />
            </label>
            <label className="field">
              Notes
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} disabled={busy} />
            </label>
          </>
        )}

        <h3>Pipeline</h3>
        {pipelines.length === 0 ? (
          <div className="muted">No pipeline is available on the hub.</div>
        ) : (
          <div className="row wrap">
            <label className="field">
              Pipeline
              <select value={pipelineName} onChange={(e) => { setPipelineName(e.target.value); setVersion(''); setSwitches({}); }} disabled={busy}>
                {pipelines.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.display_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Version
              <select value={chosenVersion?.version ?? ''} onChange={(e) => setVersion(e.target.value)} disabled={busy}>
                {pipeline?.versions
                  .filter((v) => v.state === 'available')
                  .map((v) => (
                    <option key={v.version} value={v.version}>
                      {v.version}
                      {v.is_default ? ' (default)' : ''}
                    </option>
                  ))}
              </select>
            </label>
            {allowed.includes('--frame') && (
              <label className="field">
                Frame
                <input
                  type="number"
                  min={0}
                  step={1}
                  placeholder="auto"
                  value={frame}
                  onChange={(e) => setFrame(e.target.value)}
                  disabled={busy}
                  title="Index of the cardiac phase to analyse in a 4D series; leave empty for the pipeline's choice"
                />
              </label>
            )}
          </div>
        )}
        {hub.workers.length > 0 && (
          <label className="field">
            Worker
            <select value={worker} onChange={(e) => setWorker(e.target.value)} disabled={busy}>
              <option value="">Let the hub choose (highest-spec idle worker)</option>
              {hub.workers.map((w) => (
                <option key={w.name} value={w.name}>
                  {w.name}
                  {w.gpu_name ? ` · ${w.gpu_name}${w.gpu_memory_mb ? ` ${Math.round(w.gpu_memory_mb / 1024)} GB` : ''}` : ''}
                  {!w.online ? ' · offline' : w.busy ? ' · busy' : ' · idle'}
                </option>
              ))}
            </select>
          </label>
        )}
        {worker && !hub.workers.find((w) => w.name === worker)?.online && (
          <div className="notice">{worker} is offline. The job will wait in the queue until it reconnects.</div>
        )}
        {allowed.filter((a) => a !== '--frame').length > 0 && (
          <div className="switches">
            {allowed
              .filter((a) => a !== '--frame')
              .map((flag) => (
                <label key={flag} className="check">
                  <input
                    type="checkbox"
                    checked={!!switches[flag]}
                    onChange={(e) => setSwitches({ ...switches, [flag]: e.target.checked })}
                    disabled={busy}
                  />
                  {SWITCH_LABELS[flag] ?? flag}
                </label>
              ))}
          </div>
        )}

        {reportOptions.length > 0 && (
          <>
            <h3>Reports</h3>
            <div className="switches">
              {reportOptions.map((r) => (
                <label key={r.name} className="check">
                  <input
                    type="checkbox"
                    checked={!reportsOff[r.name]}
                    onChange={(e) => setReportsOff({ ...reportsOff, [r.name]: !e.target.checked })}
                    disabled={busy}
                  />
                  {r.display_name ?? r.name}
                </label>
              ))}
            </div>
            <div className="muted small">Rendered on the worker right after the run and kept with the run.</div>
          </>
        )}

        {busy && (
          <div className="progress-block">
            <div className="muted small">
              {phase === 'creating' && 'Creating the project…'}
              {phase === 'uploading' && `Step 1 of 2 · uploading to S3… ${pct}% (${formatBytes(sent)} of ${formatBytes(total)})`}
              {phase === 'submitting' && `Step 2 of 2 · sending to the pipeline hub… ${pct}% (${formatBytes(sent)} of ${formatBytes(total)})`}
            </div>
            <div className="progress">
              <div className={`bar${phase === 'creating' ? ' indeterminate' : ''}`} style={phase !== 'creating' ? { width: `${pct}%` } : undefined} />
            </div>
          </div>
        )}
        {error && <div className="error">{error}</div>}

        <div className="row end">
          {busy ? (
            <button className="secondary" onClick={() => abort.current?.abort()} disabled={phase !== 'uploading'}>
              Cancel upload
            </button>
          ) : (
            <button className="secondary" onClick={onClose}>
              Close
            </button>
          )}
          <button onClick={() => void go()} disabled={!ready || busy}>
            {creating ? 'Create and run' : 'Run'}
          </button>
        </div>
      </div>
    </div>
  );
}
